import { useState, useCallback } from 'react';

export interface UseEntitySearchResult<T> {
  results: T[];
  loading: boolean;
  error: string | null;
  search: (query: string) => Promise<void>;
  clearResults: () => void;
}

export const useEntitySearch = <T>(
  searchFn: (query: string, limit?: number) => Promise<T[]>
): UseEntitySearchResult<T> => {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await searchFn(query.trim());
      setResults(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchFn]);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    search,
    clearResults
  };
};
